import { useId, useState } from 'react'
import type { ReactNode } from 'react'
import styles from './ChartFrame.module.css'

interface ChartFrameProps {
    title: string
    /** Caveats belong next to the chart, not in a footnote. */
    note?: string
    aside?: ReactNode
    /** The same numbers as a real table: the accessibility backstop. */
    table: ReactNode
    children: ReactNode
}

/**
 * Wraps every chart with a table view. An SVG of bars is not readable by
 * assistive technology no matter how it is labelled, so each chart ships the
 * underlying numbers in a <table> that anyone can switch to -- which turns out
 * to be useful sighted too, for copying values out.
 */
export default function ChartFrame({ title, note, aside, table, children }: ChartFrameProps) {
    const [asTable, setAsTable] = useState(false)
    const id = useId()

    return (
        <figure className={styles.frame}>
            <figcaption className={styles.head}>
                <span className={styles.title}>{title}</span>
                <span className={styles.right}>
                    {aside && <span className={styles.aside}>{aside}</span>}
                    <button
                        type="button"
                        className={styles.toggle}
                        aria-expanded={asTable}
                        aria-controls={id}
                        onClick={() => setAsTable((v) => !v)}
                    >
                        {asTable ? 'chart view' : 'table view'}
                    </button>
                </span>
            </figcaption>

            {note && <p className={styles.note}>{note}</p>}

            <div id={id} className={styles.body}>
                {asTable ? table : children}
            </div>
        </figure>
    )
}
